/**
 * Authored suggested questions and scripted answers for the agreements the
 * demo story opens. Every number is taken from the agreement text or the
 * shared data. Citations carry only a document id and a section reference;
 * qa.test.ts checks that each cited section exists in the loaded agreement.
 */
import type { ScriptedAnswer } from '../../ask/types';
import type { ContractId } from '../../data/types';
import { cite } from './ask-documents';

const GENTLY: ScriptedAnswer[] = [
  {
    question: 'What service levels does Gently AI commit to?',
    aliases: [
      'What are the SLA terms with Gently AI?',
      'What are the SLA terms?',
      'What SLA does Gently AI commit to?',
      'What are the service level terms with Gently AI?',
      'Summarize the Gently service levels',
    ],
    thinking: [
      'Reading Exhibit B: Service level agreement',
      'Cross-checking Article 13 and Section 11.4 of the master agreement',
      'Applying the $30,000 monthly fee to the credit tiers',
    ],
    answer: `Exhibit B commits Gently AI to the service levels below. Fee credits are Pillar's only money remedy for downtime (§13.3).

- **Availability:** 99.9% uptime each month. Scheduled maintenance is excluded only up to 4 hours a month with 72 hours' notice. Anything beyond that counts as downtime (B.1).
- **Support response:** urgent (Priority 1) issues within 1 hour at any time, with updates every 2 hours. Priority 2 within 4 business hours. Priority 3 within 1 business day (B.2).
- **Fee credits:** 5% of the monthly fee below 99.9%, 10% below 99.5% and 25% below 99.0%, capped at 25%. On the $30,000 monthly fee that is $1,500, $3,000 and $7,500. Pillar must request a credit within 30 days (B.3).
- **Repeated failures:** below 99.0% in any 3 months of a rolling 6 lets Pillar terminate without penalty (B.4).
- **Disaster recovery:** at most 1 hour of data lost and service restored within 4 hours, with US-only backups (B.5).`,
    citations: [
      cite('MSA-GEN-2025-001', 'B.1'),
      cite('MSA-GEN-2025-001', 'B.2'),
      cite('MSA-GEN-2025-001', 'B.3'),
      cite('MSA-GEN-2025-001', 'B.4'),
      cite('MSA-GEN-2025-001', 'B.5'),
      cite('MSA-GEN-2025-001', '13.3'),
    ],
  },
  {
    question:
      'What happens if Gently misses the uptime commitment three months in a row?',
    aliases: [
      'What happens if Gently misses the SLA three months in a row?',
      'What if Gently misses the SLA three months in a row?',
      'What if Gently misses the uptime commitment three months in a row?',
      'What if Gently has downtime three months running?',
    ],
    thinking: [
      'Reading Exhibit B, Sections B.3 and B.4',
      'Checking transition and refund terms in the master agreement',
    ],
    answer: `It depends on how far uptime falls, because credits and the exit right are separate.

- **Every missed month earns a credit.** Uptime below 99.9% credits 5%, 10% or 25% of the $30,000 monthly fee (B.3). Three months in a row at 99.4% is a 10% credit each month, $9,000 in total, and Pillar must request each one within 30 days.
- **Below 99.0% in 3 months of a rolling 6** is repeated failure. Pillar may terminate the agreement and any statement of work without penalty by notice within 60 days after the third month (B.4).
- **On exit,** Gently refunds prepaid fees and no termination charge applies. It must keep running the platform and assist with transition for up to 90 days (B.4, §11.6).`,
    citations: [
      cite('MSA-GEN-2025-001', 'B.3'),
      cite('MSA-GEN-2025-001', 'B.4'),
      cite('MSA-GEN-2025-001', '11.6'),
    ],
  },
  {
    question: 'Can Gently use our data to train AI models?',
    aliases: [
      'Can Gently train on our data?',
      'Does Gently use our data to train models?',
    ],
    thinking: [
      'Reading Article 12: Data protection, security and artificial intelligence',
      'Checking the terms for subcontractors in Exhibit C',
    ],
    answer: `No. Gently may not use Pillar data or outputs to train, fine-tune or improve any AI model, whether its own or a third party's (§12.4).

- **Subcontractors are bound too.** Each subcontractor that handles Pillar data and each third-party model provider must agree in writing to the same restriction, and to zero retention beyond the time needed to process each request (§12.4, C.3).
- **Purpose limit.** Gently may process Pillar data only to provide the services, on Pillar's documented instructions, and may not sell it (§12.2).
- **Data stays in the US,** including backups and recovery replicas (C.2).`,
    citations: [
      cite('MSA-GEN-2025-001', '12.4'),
      cite('MSA-GEN-2025-001', '12.2'),
      cite('MSA-GEN-2025-001', 'C.3'),
      cite('MSA-GEN-2025-001', 'C.2'),
    ],
  },
  {
    question: 'How much notice do we need to give to not renew?',
    aliases: [
      'What notice is needed to not renew?',
      'When is the non-renewal deadline for Gently?',
    ],
    thinking: [
      'Reading Section 11.1: Term and renewal',
      'Checking renewal pricing and termination rights',
    ],
    answer: `**60 days before the end of the term.** The initial term ends October 31, 2027, so written notice of non-renewal is due by **September 1, 2027** (§11.1). Without it the agreement renews for 12 months.

- **Gently's side:** to raise fees for a renewal it must give 120 days' notice, by July 3, 2027, and the increase cannot exceed 5% (§4.4).
- **Exit after renewal:** Pillar can also terminate for convenience on 90 days' notice at any time, with no termination charge (§11.2).`,
    citations: [
      cite('MSA-GEN-2025-001', '11.1'),
      cite('MSA-GEN-2025-001', '4.4'),
      cite('MSA-GEN-2025-001', '11.2'),
    ],
  },
];

const CORVANE: ScriptedAnswer[] = [
  {
    question: 'What are the payment terms?',
    aliases: [
      'What are the payment terms with Corvane?',
      'When do we pay Corvane?',
    ],
    thinking: [
      'Reading Article 4: Invoicing and payment',
      'Checking invoice CV-2026-08812 against the terms',
    ],
    answer: `**Net 45.** Pillar pays each undisputed invoice within 45 days after receiving a valid invoice (§4.2).

- A shorter term printed on an invoice, order acknowledgment or delivery note has no effect (§4.2). Invoice CV-2026-08812 is stamped Net 30, so it is due 45 days after receipt, not 30.
- An invoice is valid only if it cites the agreement number, the purchase order, reel lot numbers, quantity and price per 100 ft. Pillar need not pay a defective invoice until it is corrected (§4.1).
- Pillar may withhold a disputed amount by giving written reasons within 30 days (§4.4), and may set off any rebate or credit Corvane owes (§4.5).`,
    citations: [
      cite('SA-CORV-2024-017', '4.2'),
      cite('SA-CORV-2024-017', '4.1'),
      cite('SA-CORV-2024-017', '4.4'),
      cite('SA-CORV-2024-017', '4.5'),
    ],
  },
  {
    question: 'Can Corvane charge us freight?',
    aliases: [
      'Can Corvane bill freight?',
      'Who pays freight on Corvane shipments?',
    ],
    thinking: [
      'Reading Article 5: Delivery',
      'Checking invoice CV-2026-08812 for freight lines',
    ],
    answer: `**No.** Corvane delivers to the Dallas DC or the Phoenix DC with freight included and carries the freight, insurance and loading until the reels are ready for unloading at Pillar's dock (§5.1).

- No freight, insurance, handling or fuel charge may be added to the price, whether billed as a separate line, an extra charge or a surcharge (§5.2).
- Premium transport to recover a delay that Corvane caused is also at Corvane's cost (§5.2).
- Invoice CV-2026-08812 bills $3,850 of freight, which is not payable.`,
    citations: [
      cite('SA-CORV-2024-017', '5.1'),
      cite('SA-CORV-2024-017', '5.2'),
    ],
  },
  {
    question: 'How much can the price go up next year?',
    aliases: [
      'How much can Corvane raise the price?',
      'What is the price increase cap?',
    ],
    thinking: [
      'Reading Article 3: Price and price adjustment',
      'Calculating the 2026 ceiling and a 2027 scenario',
    ],
    answer: `The price follows the market price of copper and is capped at **+3% a year** (§3.3). The cap is a ceiling: Corvane cannot invoice above it.

- **2026:** the maximum is $424.36 per 100 ft, which is $412.00 plus 3%. Any invoice line above that can be rejected or paid at the contract price (§3.3). Invoice CV-2026-08812 bills 70,000 ft at $431.50 per 100 ft, which is $4,998 over.
- **2027:** this agreement expires on December 31, 2026, so it sets no 2027 price. If a +3% cap carried into a renewal, the ceiling would be $437.09 per 100 ft, above Hollis at $437.00.
- Corvane must send any adjustment with the copper price data by October 31 of the prior year, or it does not take effect (§3.3). No surcharges of any kind apply (§3.2).`,
    citations: [
      cite('SA-CORV-2024-017', '3.3'),
      cite('SA-CORV-2024-017', '3.2'),
    ],
  },
  {
    question: 'How does the volume rebate work?',
    aliases: [
      'Do we get a volume rebate from Corvane?',
      'What is the Corvane rebate?',
      'How much is the volume rebate?',
    ],
    thinking: [
      'Reading Section 3.5: Volume rebate',
      'Applying the $25M threshold to about $31M of purchases a year',
    ],
    answer: `**2% of purchases above $25M in a year**, paid back as a credit note every quarter (§3.5).

- **Only the part above $25M counts.** At about $31M a year of purchases, that is about $6M over the threshold, or roughly $120,000 a year. Purchases are counted after credits, returns and taxes (§1.1, §3.5).
- **Corvane must credit it within 30 days after each quarter ends,** against the next invoice, with a written statement of purchases and the rebate earned (§3.5). The Q3 credit has been issued.
- **Pillar can also set off** any rebate or credit Corvane owes against what it pays (§4.5).
- **Watch the renewal:** if Pillar moves 15 points of volume to Hollis, purchases fall to about $22M and the rebate stops unless the threshold is reset. The renewal letter asks for about $18M.`,
    citations: [
      cite('SA-CORV-2024-017', '3.5'),
      cite('SA-CORV-2024-017', '4.5'),
      cite('SA-CORV-2024-017', '1.1'),
    ],
  },
  {
    question: 'What happens if Corvane changes a copper-rod source?',
    aliases: [
      'What if Corvane changes a raw material source?',
      'Does Corvane have to tell us about process changes?',
    ],
    thinking: [
      'Reading Section 8.4: Change notification',
      'Checking termination and continuity-of-supply terms',
    ],
    answer: `Corvane must give Pillar **at least 180 days' written notice** of any change in plant, process or copper-rod source, and cannot implement it until Pillar has qualified and approved it in writing (§8.4).

- Corvane supplies qualification samples at its own cost, and wire made with an unapproved change is non-conforming (§8.4).
- Pillar may terminate immediately if the breach affects the quality or UL listing of the wire (§12.1).
- Corvane must also qualify a backup source for any raw material it buys from only one source and disclose single points of failure (§7.2).`,
    citations: [
      cite('SA-CORV-2024-017', '8.4'),
      cite('SA-CORV-2024-017', '12.1'),
      cite('SA-CORV-2024-017', '7.2'),
    ],
  },
  {
    question: 'What quality commitments does Corvane make?',
    aliases: [
      'What is the Corvane PPM limit?',
      'What is the Corvane defect limit?',
      'Does each reel come with a mill test report?',
      'Does each reel come with a factory test report?',
    ],
    thinking: [
      'Reading Section 8.1: Quality standards',
      'Comparing the limit of 500 defects per million with the last 12 months',
    ],
    answer: `**500 defects per million or fewer, and a factory test report with every reel lot** (§8.1).

- Over the last 12 months, defects on arrival were 820 per million, well over the limit. Four quality issues for jacket defects, short reels and mislabeled lots cost about $1.1M in returns, re-spooling and customer credits, plus 31 hours of wire-cutting downtime.
- A change of plant, process or copper-rod source needs 180 days' notice and Pillar's approval first (§8.4).
- Corvane delivers on time and in full 91.0% of the time, and late-delivery damages of 1.5% a week, capped at 10%, apply (§6.5).`,
    citations: [
      cite('SA-CORV-2024-017', '8.1'),
      cite('SA-CORV-2024-017', '8.4'),
      cite('SA-CORV-2024-017', '6.5'),
    ],
  },
  {
    question: 'When does the agreement expire and what do we need to decide?',
    aliases: [
      'When does the Corvane agreement expire?',
      'What is the Corvane renewal decision?',
    ],
    thinking: [
      'Reading Article 2: Term and non-exclusivity',
      'Checking termination rights in Article 12',
    ],
    answer: `The agreement **expires on December 31, 2026** and does not renew automatically. Any extension needs a written amendment signed by both parties (§2.1).

- Pillar should tell Corvane its renewal decision and share of volume at least 90 days before expiry, which was October 2, 2026. Nothing obliges either side to renew (§2.2).
- The agreement is non-exclusive with no minimum purchase, so Pillar can move volume to other suppliers now. The planning figure of 7,400,000 ft a year is an estimate only (§2.3).
- Pillar can terminate for convenience on 180 days' notice (§12.2).`,
    citations: [
      cite('SA-CORV-2024-017', '2.1'),
      cite('SA-CORV-2024-017', '2.2'),
      cite('SA-CORV-2024-017', '2.3'),
      cite('SA-CORV-2024-017', '12.2'),
    ],
  },
];

const HALBROOK_SOW: ScriptedAnswer[] = [
  {
    question: 'How much of the budget cap is left?',
    aliases: [
      'How much of the not-to-exceed is left?',
      'Is Halbrook over the not-to-exceed?',
      'Is Halbrook over the budget cap?',
      'How much of the NTE is left?',
    ],
    thinking: [
      'Reading Section 5.2: Budget cap',
      'Adding invoice HP-INV-55102 to cumulative billing',
    ],
    answer: `**None.** The budget cap is $450,000 for fees and expenses combined (§5.2). Pillar has been billed $371,870, and invoice HP-INV-55102 for $96,330 takes the cumulative total to **$468,200 as billed, which is $18,200 over**.

- Pillar is not obliged to pay anything above $450,000 unless a change order was signed before the work was done (§5.2, §6.1). No change order has been issued.
- Halbrook had to warn Pillar when 80% ($360,000) was billed (§5.5). No notice is on file.
- Before this invoice, $78,130 of the budget cap remained. The invoice also has $4,400 of overcharge on role rates and $3,380 of expenses over the cap, which come off first, so $10,420 is still over and $78,130 is payable, ending cumulative billing exactly at $450,000.`,
    citations: [
      cite('SOW-HALB-2026-03', '5.2'),
      cite('SOW-HALB-2026-03', '6.1'),
      cite('SOW-HALB-2026-03', '5.5'),
    ],
  },
  {
    question: 'Can Halbrook bill a senior partner rate?',
    aliases: [
      'Is there a senior partner rate?',
      'Can Halbrook bill a Senior Partner?',
    ],
    thinking: [
      'Reading Section 4.2: Hourly rates',
      'Checking invoice HP-INV-55102 for roles and rates',
    ],
    answer: `**No.** The statement of work lists four roles: Partner $950, Principal $750, Consultant $420 and Analyst $280 per hour, fixed for the statement of work (§4.2).

- Any other role needs a written change order before the role is used or billed (§4.2, §6.1).
- Invoice HP-INV-55102 bills 22 hours of "Senior Partner" at $1,150. At the Partner rate of $950 that is $4,400 less.
- The engagement team is one Partner, one Principal, two Consultants and two Analysts (§4.1).`,
    citations: [
      cite('SOW-HALB-2026-03', '4.2'),
      cite('SOW-HALB-2026-03', '4.1'),
      cite('SOW-HALB-2026-03', '6.1'),
    ],
  },
  {
    question: 'What expenses can Halbrook bill?',
    aliases: [
      'What is the expenses cap?',
      'Can Halbrook bill travel expenses?',
    ],
    thinking: [
      'Reading Section 5.3 of the statement of work and Section 6.4 of the master agreement',
      'Checking invoice HP-INV-55102 expenses against the cap',
    ],
    answer: `Expenses **cannot exceed 10% of the professional fees** invoiced under the statement of work, and each one needs an itemized receipt (§5.3).

- The master agreement adds that expenses must be at cost with no markup, follow Pillar's travel policy and be approved in advance (master agreement §6.4).
- Invoice HP-INV-55102 has $11,830 of expenses on $84,500 of fees, which is 14%. That is $3,380 over the cap.`,
    citations: [
      cite('SOW-HALB-2026-03', '5.3'),
      cite('MSA-HALB-2023-011', '6.4'),
    ],
  },
];

const HALBROOK_MSA: ScriptedAnswer[] = [
  {
    question: 'What is the liability cap?',
    aliases: [
      'What is the liability cap with Halbrook?',
      'How much is Halbrook liable for?',
    ],
    thinking: [
      'Reading Section 9.3: Limitation of liability',
      'Comparing the cap with annual fees under the master agreement',
    ],
    answer: `Each side's liability is limited to **the fees paid or payable under the statement of work concerned** (§9.3). For SOW-HALB-2026-03 that is $450,000, against about $3.8M a year of fees under the master agreement.

- Indemnification obligations, breach of confidentiality (Article 7), gross negligence and willful misconduct sit outside the cap (§9.3).
- Neither side is liable for indirect or consequential damages (§9.3).
- The playbook position is a cap of at least 12 months of fees, so this sits below it.`,
    citations: [cite('MSA-HALB-2023-011', '9.3')],
  },
  {
    question: 'Can we end the agreement early without a reason?',
    aliases: [
      'Can we terminate for convenience?',
      'Can Pillar terminate the Halbrook MSA early?',
      'How do we exit the Halbrook agreement?',
    ],
    thinking: [
      'Reading Article 11: Term and termination',
      'Checking what happens to statements of work already in effect',
    ],
    answer: `**Yes, on 30 days' written notice.** Pillar may end the master agreement or any statement of work without giving a reason, and pays only for services properly performed and expenses properly incurred to the termination date (§11.2).

- Ending the master agreement does not end a statement of work already in effect. It continues on the master agreement terms until completed or terminated (§11.1).
- Either side may terminate for an uncured material breach after 30 days' notice (§11.3).`,
    citations: [
      cite('MSA-HALB-2023-011', '11.2'),
      cite('MSA-HALB-2023-011', '11.1'),
    ],
  },
  {
    question: 'Can Halbrook bill a senior partner rate?',
    aliases: [
      'Is there a senior partner rate?',
      'Can Halbrook bill a Senior Partner?',
    ],
    thinking: [
      'Reading Section 6.1: Hourly rates',
      'Reading the hourly rates in SOW-HALB-2026-03',
    ],
    answer: `**No.** Rates are those listed in the applicable statement of work, fixed for that statement of work, and no role or rate outside the list may be billed (master agreement §6.1).

- SOW-HALB-2026-03 lists Partner $950, Principal $750, Consultant $420 and Analyst $280 per hour (statement of work §4.2).
- A role that is not listed can be billed only if a written change order adds it before the work is done (§6.1).
- Invoice HP-INV-55102 bills 22 hours of "Senior Partner" at $1,150, which is not among the agreed rates.`,
    citations: [
      cite('MSA-HALB-2023-011', '6.1'),
      cite('SOW-HALB-2026-03', '4.2'),
    ],
  },
];

const STRATUS: ScriptedAnswer[] = [
  {
    question: 'When is the notice deadline to stop the auto-renewal?',
    aliases: [
      'When is the Stratus notice deadline?',
      'How do we stop the Stratus auto-renewal?',
    ],
    thinking: [
      'Reading Article 5: Term and renewal',
      'Checking notice requirements in Section 11.2',
    ],
    answer: `**November 2, 2026.** The agreement renews automatically for 36 months after January 31, 2027 unless Pillar gives written notice of non-renewal at least 90 days before the Initial Term ends (§5.2).

- **Delivery matters.** Notice must go by hand or overnight courier to the Stratus General Counsel in Seattle. Email alone is not effective (§11.2).
- **Cost of missing it.** The renewal keeps the $12M annual commitment, $36M over 36 months (§5.3).
- **No easy exit later.** Pillar cannot terminate for convenience except by paying the unpaid balance of that year's commitment (§5.5).`,
    citations: [
      cite('EA-STRT-2024-001', '5.2'),
      cite('EA-STRT-2024-001', '11.2'),
      cite('EA-STRT-2024-001', '5.3'),
      cite('EA-STRT-2024-001', '5.5'),
    ],
  },
  {
    question: 'What happens if we do not use the full $12M commitment?',
    aliases: [
      'What if we under-consume the Stratus commitment?',
      'Can unused Stratus spend roll over?',
    ],
    thinking: [
      'Reading Article 3: Committed spend and fees',
      'Applying the 18% under-consumption to the $12M commitment',
    ],
    answer: `**The unused balance is lost.** There is no refund, and only up to 10% of the commitment ($1.2M) can be carried into the next contract year (§3.3).

- Consumption is tracking 18% under the commitment, about $2.16M a year, so about $0.96M a year is forfeited beyond the rollover.
- Pillar owes nothing beyond the twelve $1,000,000 monthly installments because of under-consumption (§3.3).
- Stratus must meet within 30 days of a written request to review usage and discuss rebalancing the commitment in good faith (§3.4).`,
    citations: [
      cite('EA-STRT-2024-001', '3.3'),
      cite('EA-STRT-2024-001', '3.4'),
    ],
  },
  {
    question:
      'What credit do we get if Stratus misses the 99.95% uptime promise?',
    aliases: [
      'What credit do we get if Stratus misses the 99.95% SLA?',
      'What is the Stratus SLA credit?',
      'What is the Stratus uptime credit?',
      'What credit is owed for the September outage?',
    ],
    thinking: [
      'Reading Article 6: Service levels',
      'Checking September availability against the 99.95% commitment',
    ],
    answer: `**10% of the monthly fees**, which is $100,000 at the $12M commitment (§6.2). The promise is 99.95% availability in each calendar month (§6.1).

- Stratus must apply the credit automatically to the next invoice, with no claim from Pillar (§6.3).
- September availability was 99.81%, so $100,000 is owed. It was not applied on invoice STR-INV-20931.
- Stratus must report availability within 10 business days after each month (§6.4).`,
    citations: [
      cite('EA-STRT-2024-001', '6.1'),
      cite('EA-STRT-2024-001', '6.2'),
      cite('EA-STRT-2024-001', '6.3'),
      cite('EA-STRT-2024-001', '6.4'),
    ],
  },
];

const LUMEN: ScriptedAnswer[] = [
  {
    question: 'Can Lumen add a tariff surcharge?',
    aliases: [
      'Can Lumen pass tariffs through to us?',
      'Is Lumen allowed to surcharge for tariffs?',
    ],
    thinking: [
      'Reading Article 5: Delivery, duties and tariffs',
      'Checking invoice LB-26-3391 against Sections 4.3 and 5.3',
    ],
    answer: `**No.** Lumen delivers to the Hayward DC with duties and tariffs paid and bears all duties, tariffs and import taxes, including any new or increased charge introduced after the effective date (§5.1, §5.3).

- Lumen may not invoice them as a surcharge, pass-through or adjustment, and no tariff change lets it change the price (§5.3).
- The price of $5,500 per drive is fixed for the term (§4.1), and an invoice may contain no line other than the product at that price (§4.3).
- A tariff change is not force majeure (§12.3), and Pillar may terminate immediately if Lumen breaches §5.3 (§11.1).
- Invoice LB-26-3391 adds a 25% tariff surcharge of $82,500, so that line is invalid.`,
    citations: [
      cite('SA-LUMN-2025-002', '5.1'),
      cite('SA-LUMN-2025-002', '5.3'),
      cite('SA-LUMN-2025-002', '4.1'),
      cite('SA-LUMN-2025-002', '4.3'),
      cite('SA-LUMN-2025-002', '12.3'),
      cite('SA-LUMN-2025-002', '11.1'),
    ],
  },
  {
    question: 'Can we qualify a second supplier?',
    aliases: [
      'Can we qualify a second source?',
      'Can we buy 50 HP VFDs from another supplier?',
      'Can we buy motor drives from another supplier?',
      'Is Lumen exclusive?',
    ],
    thinking: [
      'Reading Section 2.2: Non-exclusivity and alternative sources',
      'Reading Article 7: Continuity of supply',
    ],
    answer: `**Yes.** The agreement is non-exclusive, Pillar has no minimum purchase, and Lumen must cooperate with qualifying an alternative source at no charge (§2.2).

- If Lumen cannot supply, it must reserve at least Pillar’s share of recent sales for Pillar. After 30 days of failure to supply, Pillar may buy elsewhere and Lumen reimburses the reasonable excess cost (§7.3).
- Lumen is the only supplier today: all 3,400 of the 50 HP motor drives Pillar buys each year, at $5,500 each delivered with duties paid, about $18.7M.
- Lumen holds 90 days of reserve stock at its own cost (§7.1).
- Kestrel, quoted at $6,050 per drive, is in onboarding as the second supplier (ONB-0087).`,
    citations: [
      cite('SA-LUMN-2025-002', '2.2'),
      cite('SA-LUMN-2025-002', '7.3'),
      cite('SA-LUMN-2025-002', '7.1'),
    ],
  },
  {
    question: 'When does the agreement end and what are our exit rights?',
    aliases: [
      'When does the Lumen agreement expire?',
      'Can we terminate the Lumen agreement early?',
    ],
    thinking: ['Reading Section 2.1: Term', 'Reading Article 11: Termination'],
    answer: `The agreement **ends on June 30, 2027** and can be extended only by a written amendment signed by both parties (§2.1).

- Pillar may terminate for convenience on 120 days' notice. Lumen may not (§11.2).
- After expiry or termination, Lumen must keep supplying at the same price for up to 6 months if Pillar asks, to cover a transition (§11.3).`,
    citations: [
      cite('SA-LUMN-2025-002', '2.1'),
      cite('SA-LUMN-2025-002', '11.2'),
      cite('SA-LUMN-2025-002', '11.3'),
    ],
  },
];

const TRANSCON: ScriptedAnswer[] = [
  {
    question: 'What is the cap on the fuel surcharge?',
    aliases: [
      'What is the fuel surcharge limit?',
      'How high can the fuel surcharge go?',
    ],
    thinking: [
      'Reading Article 5: Fuel and extra charges',
      'Checking invoice TCF-778120 against the diesel price table',
    ],
    answer: `**18% of the base freight charge**, however high diesel prices go (§5.2). Below that cap the surcharge follows the U.S. Department of Energy diesel price table: 0% under $3.60 a gallon, then 4 points for each further $0.20 (§5.2).

- It must appear as its own invoice line with the diesel price and the percentage applied. No fuel surcharge applies to extra charges (§5.2).
- Invoice TCF-778120 charges 24% ($11,520 on $48,000 of base freight). The cap allows $8,640, so $2,880 is over.`,
    citations: [cite('MSA-TRAN-2025-006', '5.2')],
  },
  {
    question: 'When can Transcontinental charge for waiting time?',
    aliases: [
      'When can Transcontinental charge detention?',
      'When is detention payable?',
      'What proof does detention need?',
      'When is waiting time payable?',
    ],
    thinking: [
      'Reading Section 5.4: Waiting time',
      'Checking invoice TCF-778120 for waiting-time proof',
    ],
    answer: `Only when **all three** conditions are met (§5.4):

- The equipment was held more than 2 hours after the appointment time, and not because the carrier arrived late.
- The carrier submitted proof signed by an authorized representative of the Pillar facility, such as a signed gate log or bill of lading.
- The claim was submitted within 30 days after the shipment.

The rate is $75 an hour after the first 2 hours. Pillar need not pay waiting time without the signed proof. Invoice TCF-778120 bills $1,150 of waiting time with no signed proof.`,
    citations: [cite('MSA-TRAN-2025-006', '5.4')],
  },
  {
    question: 'Can Transcontinental add other fees?',
    aliases: [
      'Can the carrier add accessorial fees?',
      'Can the carrier add extra charges?',
      'What other charges can Transcontinental bill?',
    ],
    thinking: ['Reading Sections 5.1 and 5.5: Charges payable'],
    answer: `**Not without Pillar's written agreement.** The only charges payable beyond base freight are the fuel surcharge and the listed extra charges (§5.1).

- Administrative, technology, security, environmental, peak-season and equipment fees are excluded unless agreed in a written amendment (§5.1).
- Any other charge, such as lumper, layover or tarping, is payable only if Pillar approved it in writing before the service was performed (§5.5).`,
    citations: [
      cite('MSA-TRAN-2025-006', '5.1'),
      cite('MSA-TRAN-2025-006', '5.5'),
    ],
  },
];

const VITREX: ScriptedAnswer[] = [
  {
    question:
      'What notice does Vitrex owe us before changing a manufacturing site?',
    aliases: [
      'What notice must Vitrex give for a site change?',
      'Can Vitrex change its manufacturing site?',
    ],
    thinking: [
      'Reading Article 7: Change notice and approval',
      'Checking lot VX-24117 against Section 7.2',
    ],
    answer: `**At least 180 days' written notice, and Pillar's written approval first.** This covers any change of manufacturing site, subcontractor or component source, and the 800 A breakers may ship only from factories UL has certified (§7.2).

- Lot VX-24117 was made at a factory UL has not certified and that was never disclosed. That is a critical change made without notice, so §7.2 was breached.
- Other process or specification changes need 90 days' notice and Pillar's written confirmation (§7.3).`,
    citations: [
      cite('QA-VITX-2023-004', '7.2'),
      cite('QA-VITX-2023-004', '7.3'),
    ],
  },
  {
    question: 'What can we do about lots made at an unapproved factory?',
    aliases: [
      'Can we reject the affected lots?',
      'What remedies do we have for VX-24117?',
    ],
    thinking: ['Reading Sections 7.5, 9.2 and 9.5'],
    answer: `Pillar may **reject any affected lot, whether or not it has been used**, and Vitrex must replace or credit it (§7.5).

- Vitrex must also reimburse the resulting costs, including investigation, quarantine, retesting, rework, replacement breakers and control panel shop downtime (§7.5).
- Vitrex must quarantine related lots immediately and tell Pillar about related lots shipped elsewhere (§9.2). Lot VX-24117 is quarantined at the control panel shop, with about 6 weeks of supply on hand.
- If Vitrex responds late or defective product ships again, Pillar may put it on controlled shipping or enhanced inspection at Vitrex's cost (§9.5).
- Hollis is the qualified backup supplier for 800 A breakers, at $4,020 each against $3,850.`,
    citations: [
      cite('QA-VITX-2023-004', '7.5'),
      cite('QA-VITX-2023-004', '9.2'),
      cite('QA-VITX-2023-004', '9.5'),
    ],
  },
  {
    question: 'How fast must Vitrex respond to a corrective action request?',
    aliases: [
      'How fast must Vitrex respond to a SCAR?',
      'What is the corrective action response time?',
      'What is the SCAR response time?',
      'How long does Vitrex have to respond to SCAR-0192?',
    ],
    thinking: ['Reading Section 9.1: Supplier corrective action requests'],
    answer: `Vitrex must **acknowledge within 2 business days** and **respond within 10 business days** with a documented cause, immediate containment actions and a plan to correct and prevent it, with owners and dates (§9.1).

- If it cannot finish in time it must give an interim response, and Pillar's quality team agrees a revised date in writing (§9.1).
- Corrective actions must be verified over at least 3 consecutive lots or 90 days, whichever is longer, before the request is closed (§9.4).
- SCAR-0192 is open for lot VX-24117.`,
    citations: [
      cite('QA-VITX-2023-004', '9.1'),
      cite('QA-VITX-2023-004', '9.4'),
    ],
  },
];

export const AUTHORED_QA: Partial<Record<ContractId, ScriptedAnswer[]>> = {
  'MSA-GEN-2025-001': GENTLY,
  'SA-CORV-2024-017': CORVANE,
  'SOW-HALB-2026-03': HALBROOK_SOW,
  'MSA-HALB-2023-011': HALBROOK_MSA,
  'EA-STRT-2024-001': STRATUS,
  'SA-LUMN-2025-002': LUMEN,
  'MSA-TRAN-2025-006': TRANSCON,
  'QA-VITX-2023-004': VITREX,
  'SA-VITX-2023-004': VITREX,
};
